import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Layout from '@/components/Layout';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { BarChart3, TrendingUp, CheckCircle, Target, Award, Calendar, Download } from 'lucide-react';

import { BACKEND_URL } from '../../config/api';
const API = `${BACKEND_URL}/api`;

const getAuthHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem('lms_token')}`
});

export default function Report({ user, onLogout }) {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedCourse, setSelectedCourse] = useState('all');

  useEffect(() => {
    fetchReport();
  }, []);

  const fetchReport = async () => {
    try {
      const res = await axios.get(`${API}/reports/student`, { headers: getAuthHeaders() });
      setReport(res.data);
    } catch (error) {
      console.error('Failed to fetch report:', error);
    } finally {
      setLoading(false);
    }
  };

  const downloadReport = () => {
    if (!report) return;

    const filteredCourses = selectedCourse === 'all' 
      ? report.course_reports 
      : report.course_reports.filter(c => c.course_id === selectedCourse);

    let csvContent = "Student Performance Report\n\n";
    csvContent += `Student Name:,${report.student_info.name}\n`;
    csvContent += `Email:,${report.student_info.email}\n`;
    csvContent += `XP Points:,${report.student_info.xp_points}\n`;
    csvContent += `Daily Streak:,${report.student_info.daily_streak}\n\n`;
    
    csvContent += "Overall Statistics\n";
    csvContent += `Total Courses:,${selectedCourse === 'all' ? report.overall_stats.total_courses : filteredCourses.length}\n`;
    csvContent += `Total Lessons Completed:,${report.overall_stats.total_lessons_completed}\n`;
    csvContent += `Total Quizzes Taken:,${report.overall_stats.total_quizzes_taken}\n`;
    csvContent += `Total Quizzes Passed:,${report.overall_stats.total_quizzes_passed}\n`;
    csvContent += `Pass Rate:,${report.overall_stats.pass_rate}%\n\n`;
    
    csvContent += "Course-wise Performance\n";
    csvContent += "Course Title,Total Lessons,Completed Lessons,Progress %,Quizzes Taken,Quizzes Passed,Average Score\n";
    
    filteredCourses.forEach(course => {
      csvContent += `${course.course_title},${course.total_lessons},${course.completed_lessons},${course.progress_percentage},${course.quizzes_taken},${course.quizzes_passed},${course.average_score}%\n`;
    });

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `student_report_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (loading) {
    return <Layout user={user} onLogout={onLogout} currentPage="reports"><div className="p-8">Loading...</div></Layout>;
  }

  if (!report) {
    return <Layout user={user} onLogout={onLogout} currentPage="reports"><div className="p-8">No report data available</div></Layout>;
  }

  const { student_info, overall_stats, course_reports } = report;

  return (
    <Layout user={user} onLogout={onLogout} currentPage="reports">
      <div className="p-6 lg:p-8 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-4xl lg:text-5xl font-bold mb-2" style={{ color: '#d86a2e' }}>📊 Your Report</h1>
            <p className="text-lg text-gray-600">Track your learning progress and achievements</p>
          </div>
          <div className="hidden lg:block">
            <div className="text-6xl">📈</div>
          </div>
        </div>

        {/* Filter and Download */}
        <Card className="border-0 shadow-lg" style={{ background: 'linear-gradient(135deg, #fff5eb 0%, #ffe8d6 100%)' }}>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <label className="font-semibold" style={{ color: '#d86a2e' }}>Filter by Course:</label>
                <select
                  value={selectedCourse}
                  onChange={(e) => setSelectedCourse(e.target.value)}
                  className="px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-400"
                >
                  <option value="all">All Courses</option>
                  {course_reports.map((course) => (
                    <option key={course.course_id} value={course.course_id}>{course.course_title}</option>
                  ))}
                </select>
              </div>
              <Button
                onClick={downloadReport}
                className="font-semibold"
                style={{ background: '#ff9d5c' }}
              >
                <Download className="w-4 h-4 mr-2" />
                Download Report
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Student Info Card */}
        <Card className="border-0 shadow-lg" style={{ background: 'linear-gradient(135deg, #fff5eb 0%, #ffe8d6 100%)' }}>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-semibold text-gray-600 uppercase mb-1">Student Profile</p>
                <h2 className="text-3xl font-bold" style={{ color: '#d86a2e' }}>{student_info.name}</h2>
                <p className="text-gray-600 mt-1">{student_info.email}</p>
              </div>
              <div className="flex space-x-6">
                <div className="text-center">
                  <div className="w-16 h-16 rounded-2xl flex items-center justify-center shadow-lg mb-2" style={{ background: 'linear-gradient(135deg, #ff9d5c, #ff7f3e)' }}>
                    <Award className="w-8 h-8 text-white" />
                  </div>
                  <p className="text-2xl font-black" style={{ color: '#d86a2e' }}>{student_info.xp_points}</p>
                  <p className="text-xs text-gray-600">XP Points</p>
                </div>
                <div className="text-center">
                  <div className="w-16 h-16 rounded-2xl flex items-center justify-center shadow-lg mb-2" style={{ background: 'linear-gradient(135deg, #ff9d5c, #ff7f3e)' }}>
                    <Calendar className="w-8 h-8 text-white" />
                  </div>
                  <p className="text-2xl font-black" style={{ color: '#d86a2e' }}>{student_info.daily_streak}</p>
                  <p className="text-xs text-gray-600">Day Streak</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Overall Statistics */}
        <div className="space-y-4">
          <h2 className="text-2xl font-bold" style={{ color: '#d86a2e' }}>📊 Overall Statistics</h2>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <Card className="border-0 shadow-lg" style={{ background: 'linear-gradient(135deg, #FFE5D0 0%, #FFDAB9 100%)' }}>
              <CardContent className="p-5">
                <div className="flex items-start justify-between mb-3">
                  <div className="w-12 h-12 rounded-2xl flex items-center justify-center shadow-md" style={{ background: 'linear-gradient(135deg, #ff9d5c, #ff7f3e)' }}>
                    <Target className="w-6 h-6 text-white" />
                  </div>
                  <div className="text-3xl">📚</div>
                </div>
                <p className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1">Total Courses</p>
                <p className="text-4xl font-black" style={{ color: '#d86a2e' }}>{overall_stats.total_courses}</p>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-lg" style={{ background: 'linear-gradient(135deg, #FFE8CC 0%, #FFD4A3 100%)' }}>
              <CardContent className="p-5">
                <div className="flex items-start justify-between mb-3">
                  <div className="w-12 h-12 rounded-2xl flex items-center justify-center shadow-md" style={{ background: 'linear-gradient(135deg, #ff9d5c, #ff7f3e)' }}>
                    <CheckCircle className="w-6 h-6 text-white" />
                  </div>
                  <div className="text-3xl">✅</div>
                </div>
                <p className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1">Lessons Completed</p>
                <p className="text-4xl font-black" style={{ color: '#d86a2e' }}>{overall_stats.total_lessons_completed}</p>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-lg" style={{ background: 'linear-gradient(135deg, #FFF0E0 0%, #FFE5CC 100%)' }}>
              <CardContent className="p-5">
                <div className="flex items-start justify-between mb-3">
                  <div className="w-12 h-12 rounded-2xl flex items-center justify-center shadow-md" style={{ background: 'linear-gradient(135deg, #ff9d5c, #ff7f3e)' }}>
                    <BarChart3 className="w-6 h-6 text-white" />
                  </div>
                  <div className="text-3xl">🎯</div>
                </div>
                <p className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1">Quizzes Taken</p>
                <p className="text-4xl font-black" style={{ color: '#d86a2e' }}>{overall_stats.total_quizzes_taken}</p>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-lg" style={{ background: 'linear-gradient(135deg, #FFEFD5 0%, #FFE4B5 100%)' }}>
              <CardContent className="p-5">
                <div className="flex items-start justify-between mb-3">
                  <div className="w-12 h-12 rounded-2xl flex items-center justify-center shadow-md" style={{ background: 'linear-gradient(135deg, #ff9d5c, #ff7f3e)' }}>
                    <TrendingUp className="w-6 h-6 text-white" />
                  </div>
                  <div className="text-3xl">📈</div>
                </div>
                <p className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1">Pass Rate</p>
                <p className="text-4xl font-black" style={{ color: '#d86a2e' }}>{overall_stats.pass_rate}<span className="text-2xl">%</span></p>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Course-wise Performance */}
        <div className="space-y-4">
          <h2 className="text-2xl font-bold" style={{ color: '#d86a2e' }}>📝 Course-wise Performance</h2>
          {(selectedCourse === 'all' ? course_reports : course_reports.filter(c => c.course_id === selectedCourse)).length === 0 ? (
            <Card className="border-0 shadow-lg" style={{ background: 'linear-gradient(135deg, #fff5eb 0%, #ffe8d6 100%)' }}>
              <CardContent className="p-12 text-center">
                <div className="text-6xl mb-4">📚</div>
                <p className="text-xl font-semibold" style={{ color: '#d86a2e' }}>No courses enrolled yet</p>
                <p className="text-gray-600 mt-2">Start learning to see your progress here!</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {(selectedCourse === 'all' ? course_reports : course_reports.filter(c => c.course_id === selectedCourse)).map((course) => (
                <Card key={course.course_id} className="border-0 shadow-lg">
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between mb-4">
                      <div>
                        <h3 className="text-xl font-bold mb-1">{course.course_title}</h3>
                        <p className="text-sm text-gray-600">Progress: {course.progress_percentage}%</p>
                      </div>
                      <div className="text-right">
                        <p className="text-3xl font-black" style={{ color: '#d86a2e' }}>{course.progress_percentage}%</p>
                      </div>
                    </div>
                    
                    <Progress value={course.progress_percentage} className="h-3 mb-4" style={{ background: '#e5e7eb' }} />
                    
                    <div className="grid grid-cols-4 gap-4">
                      <div className="text-center p-3 rounded-lg" style={{ background: '#fff5eb' }}>
                        <p className="text-2xl font-bold" style={{ color: '#d86a2e' }}>{course.completed_lessons}/{course.total_lessons}</p>
                        <p className="text-xs text-gray-600">Lessons</p>
                      </div>
                      <div className="text-center p-3 rounded-lg" style={{ background: '#fff5eb' }}>
                        <p className="text-2xl font-bold" style={{ color: '#d86a2e' }}>{course.quizzes_taken}</p>
                        <p className="text-xs text-gray-600">Quizzes</p>
                      </div>
                      <div className="text-center p-3 rounded-lg" style={{ background: '#fff5eb' }}>
                        <p className="text-2xl font-bold" style={{ color: '#d86a2e' }}>{course.quizzes_passed}</p>
                        <p className="text-xs text-gray-600">Passed</p>
                      </div>
                      <div className="text-center p-3 rounded-lg" style={{ background: '#fff5eb' }}>
                        <p className="text-2xl font-bold" style={{ color: '#d86a2e' }}>{course.average_score}%</p>
                        <p className="text-xs text-gray-600">Avg Score</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>

        {/* Performance Insights */}
        <Card className="border-0 shadow-lg" style={{ background: 'linear-gradient(135deg, #FFF5EB 0%, #FFEBD6 100%)' }}>
          <CardContent className="p-6">
            <h3 className="text-xl font-bold mb-4" style={{ color: '#d86a2e' }}>💡 Performance Insights</h3>
            <div className="space-y-3">
              {overall_stats.pass_rate >= 80 && (
                <div className="flex items-start space-x-3 p-3 rounded-lg" style={{ background: '#d1fae5' }}>
                  <div className="text-2xl">🎉</div>
                  <div>
                    <p className="font-semibold text-green-700">Excellent Performance!</p>
                    <p className="text-sm text-green-600">You're maintaining a great pass rate of {overall_stats.pass_rate}%</p>
                  </div>
                </div>
              )}
              {student_info.daily_streak >= 7 && (
                <div className="flex items-start space-x-3 p-3 rounded-lg" style={{ background: '#ffedd5' }}>
                  <div className="text-2xl">🔥</div>
                  <div>
                    <p className="font-semibold text-orange-700">Consistent Learner!</p>
                    <p className="text-sm text-orange-600">Keep up your {student_info.daily_streak}-day streak!</p>
                  </div>
                </div>
              )}
              {overall_stats.total_lessons_completed === 0 && (
                <div className="flex items-start space-x-3 p-3 rounded-lg" style={{ background: '#dbeafe' }}>
                  <div className="text-2xl">🎯</div>
                  <div>
                    <p className="font-semibold text-blue-700">Start Your Journey!</p>
                    <p className="text-sm text-blue-600">Complete your first lesson to start earning XP points</p>
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}